import { Fragment, useEffect, useState, useMemo } from "react";
import {
  PDFViewer,
  Document,
  Page,
  Text,
  View,
  pdf, // ✅ Added for blob generation
} from "@react-pdf/renderer";
import styles from "./PDFReportViewer.module.css";
import styled from "styled-components";
import Button from "../../components/Button";
import { registerPoppins } from "../../fonts/registerFonts";
import backIcon from "../../assets/back_icon.svg";
import AntRadioGroup from "../../components/Ant/AntRadioGroup/AntRadioGroup";
import { useTranslation } from "react-i18next";
import { getLocalizedDate } from "../../functions/localization";
import { Pagination } from "antd";
import {
  PDF_PREVIEW_ROWS,
  getPdfPreviewPageCount,
  formatPdfFooter,
} from "./reportExport";

registerPoppins();

const ROWS_PER_PDF_PAGE = 40;

const MyView = styled.div`
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  padding: 30px 20px;
  height: 100%;
  max-height: 1024px;
  min-height: 100%;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 75px;
  min-height: 75px;
  border-bottom: 1px solid #a7bbcf;
`;

const TitleGroup = styled.div`
  display: flex;
  align-items: center;
  width: 304px;
  min-width: 304px;
`;

const BackButton = styled.img`
  cursor: pointer;
  width: 30px;
  height: 30px;
  margin-right: 20px;
`;

const ReportTitle = styled.p`
  margin: 0;
  font-family: "Poppins";
  font-style: normal;
  font-weight: normal;
  font-size: 24px;
  line-height: 33px;
  color: #454b60;
  min-width: 160px;
  width: 160px;
`;

const PDFReportViewer = ({
  user,
  title,
  headings,
  data,
  collectionSequence,
  onBack,
  columnSums,
  columnAverages,
  selectionRange,
  showAVGRow,
  showSUMRow,
  headingsSpacing,
  dataSpacing,
  defaultFontSize,
}) => {
  const { t } = useTranslation("pdfReportViewer");

  const pageWidth = 760;
  const columnWidth = pageWidth / headings.length;
  const charWidthInPx = 6.4;

  const [columnWidthTypeRadioValue, setColumnWidthTypeRadioValue] = useState(1);
  const [fontSizeRadioValue, setFontSizeRadioValue] = useState(() =>
    defaultFontSize?.toLowerCase() === "small" ? 2 : 1,
  );
  const [headingsAndDataFontSize, setHeadingsAndDataFontSize] = useState(() =>
    defaultFontSize?.toLowerCase() === "small" ? 8 : 10,
  );
  const [sumAndAvgFontSize, setSumAndAvgFontSize] = useState(() =>
    defaultFontSize?.toLowerCase() === "small" ? 10 : 12,
  );

  let allowCustomWidths =
    headingsSpacing?.length === headings.length &&
    dataSpacing?.length === headings.length;

  useEffect(() => {
    if (allowCustomWidths) {
      setColumnWidthTypeRadioValue(3);
    }
  }, []);

  const tempVariableColumnWidthsArray = headings.map((heading) => {
    const parts = heading.trim().split(" ");
    const maxLength = Math.max(...parts.map((part) => part.length));
    return maxLength * charWidthInPx;
  });

  /* JFCRX-6738 (folded in from JFCRX-6794). CONTRACT: `data` is a flat array of
   * ROWS. It is never an array of groups, and this component no longer accepts
   * a `groupedField` prop -- callers flatten before handing the data over.
   *
   * It used to accept groups and flatten them into `tableRowsData`, but that
   * flattening fed ONLY the width scan below; the document itself was handed
   * the unflattened `data` -- by `previewDoc` and by `handleDownload`, named
   * rather than given line numbers because this file keeps shifting. So widths
   * were measured from rows
   * while the document drew groups -- and on a caller using custom widths it
   * did not merely look wrong, it threw, because `dataSpacing[cIdx]` runs off
   * the end once cIdx indexes a group's rows rather than a row's cells.
   *
   * The ternary is deleted rather than left as a no-op, and `groupedField` is
   * removed from the props rather than left destructured and unread -- this
   * change is what made it dead. Same shape JFCRX-6782 landed on the
   * Substitution copy: flatten at the call site, and let the document receive
   * rows in every case. */
  data.forEach((row) => {
    row.forEach((val, j) => {
      // JFCRX-7643. A null or undefined cell measures as empty text; it used
      // to throw on .toString() and take the whole preview down with it.
      const strVal =
        val == null
          ? ""
          : typeof val === "string"
            ? val.substring(0, 750)
            : String(val);
      const substrings = strVal.split("\n");
      substrings.forEach((s) => {
        const width = Math.min(s.length * charWidthInPx, 200);
        if (width > tempVariableColumnWidthsArray[j]) {
          tempVariableColumnWidthsArray[j] = width;
        }
      });
    });
  });

  const totalColumnWidth = tempVariableColumnWidthsArray.reduce(
    (sum, w) => sum + w,
    0,
  );
  const proportionalityCorrectionFactor = 758 / totalColumnWidth;
  const variableColumnWidthsArray = tempVariableColumnWidthsArray.map(
    (w) => w * proportionalityCorrectionFactor,
  );

  let startDate = selectionRange?.startDate
    ? getLocalizedDate(
        selectionRange.startDate,
        user?.defaultSite?.shippingAddress?.country,
      )
    : null;
  let endDate = selectionRange?.endDate
    ? getLocalizedDate(
        selectionRange.endDate,
        user?.defaultSite?.shippingAddress?.country,
      )
    : null;

  const [currentPage, setCurrentPage] = useState(0);
  const previewData = useMemo(
    () =>
      data.slice(
        currentPage * PDF_PREVIEW_ROWS,
        (currentPage + 1) * PDF_PREVIEW_ROWS,
      ),
    [data, currentPage],
  );
  const fontSize = defaultFontSize === "small" ? 8 : 10;

  /* JFCRX-7643. The preview is one ten-row document per pager page, so
   * react-pdf's own counter inside it always reads "1 / 1". The preview
   * footer states the preview position instead, counted the same way the
   * pager counts. The download passes no label and keeps real page numbers. */
  const previewFooterLabel = t("previewPageLabel", {
    page: currentPage + 1,
    total: getPdfPreviewPageCount(data.length),
  });

  const createPDFDocument = ({
    title,
    headings,
    data,
    columnWidth,
    fontSize,
    startDate,
    endDate,
    isDownload = false,
    footerLabel,
  }) => {
    const pages = [];

    const headerView = (
      <View
        fixed
        style={{
          flexDirection: "row",
          justifyContent: "space-between",
          fontSize: 10,
          fontWeight: 500,
          marginBottom: 10,
        }}
      >
        <Text>{title}</Text>
        <Text>{startDate && endDate ? `${startDate} - ${endDate}` : ""}</Text>
      </View>
    );

    const headingsRow = (
      <View
        fixed
        style={{
          flexDirection: "row",
          borderBottom: "2px solid #089BAB",
          paddingVertical: 12,
        }}
      >
        {headings.map((heading, i) => {
          let width = columnWidth;
          if (columnWidthTypeRadioValue === 1)
            width = variableColumnWidthsArray[i];
          else if (columnWidthTypeRadioValue === 3)
            width = headingsSpacing[i].columnWidth;

          return (
            <Text
              key={i}
              style={{
                width,
                fontSize: headingsAndDataFontSize,
                fontWeight: 500,
                paddingRight: 5,
              }}
            >
              {heading}
            </Text>
          );
        })}
      </View>
    );

    /* JFCRX-7643. At least one page, so a report with no rows still shows
     * its header, headings and footer rather than a blank viewer. */
    const pageCount = Math.max(1, Math.ceil(data.length / ROWS_PER_PDF_PAGE));
    for (let p = 0; p < pageCount; p += 1) {
      const i = p * ROWS_PER_PDF_PAGE;
      const pageRows = data.slice(i, i + ROWS_PER_PDF_PAGE);
      const isLastPage = p === pageCount - 1;

      pages.push(
        <Page
          key={i}
          size="LETTER"
          orientation="landscape"
          style={{ padding: 20, fontFamily: "Poppins" }}
        >
          {headerView}
          {headingsRow}
          {pageRows.map((row, rIdx) => (
            <View
              key={rIdx}
              style={{
                flexDirection: "row",
                backgroundColor: rIdx % 2 ? "#EBEDF8" : "#FFFFFF",
                paddingVertical: 5,
              }}
            >
              {row.map((cell, cIdx) => {
                let width = columnWidth;
                if (columnWidthTypeRadioValue === 1)
                  width = variableColumnWidthsArray[cIdx];
                else if (columnWidthTypeRadioValue === 3)
                  width = dataSpacing[cIdx].columnWidth;

                return (
                  <Text
                    key={cIdx}
                    style={{
                      width,
                      fontSize: headingsAndDataFontSize,
                      paddingRight: 5,
                    }}
                  >
                    {typeof cell === "string"
                      ? cell.replace(/-/g, "\u00AD")
                      : cell}
                  </Text>
                );
              })}
            </View>
          ))}
          <Text
            fixed
            style={{
              position: "absolute",
              bottom: 6,
              width: "100%",
              textAlign: "center",
              fontSize: 10,
            }}
            render={({ pageNumber, totalPages }) =>
              formatPdfFooter({ footerLabel, pageNumber, totalPages })
            }
          />

          {/* SUM/AVG rows (optional) */}
          {isDownload && isLastPage && (
            <Fragment>
              {showSUMRow && (
                <>
                  <Text
                    style={{
                      fontSize: sumAndAvgFontSize,
                      fontWeight: 500,
                      color: "#089BAB",
                      paddingTop: 12,
                    }}
                  >
                    {t("sumCol")}
                  </Text>
                  <View style={{ flexDirection: "row", paddingTop: 10 }}>
                    {columnSums.map((total, i) => (
                      <Text
                        key={i}
                        style={{
                          width: variableColumnWidthsArray[i],
                          fontSize: sumAndAvgFontSize,
                          paddingRight: 5,
                        }}
                      >
                        {total >= 0
                          ? total.toFixed(2).replace(/(\.\d*[1-9])0+$/, "$1")
                          : "--"}
                      </Text>
                    ))}
                  </View>
                </>
              )}
              {showAVGRow && (
                <>
                  <Text
                    style={{
                      fontSize: sumAndAvgFontSize,
                      fontWeight: 500,
                      color: "#089BAB",
                      paddingTop: 12,
                    }}
                  >
                    {t("averageCol")}
                  </Text>
                  <View style={{ flexDirection: "row", paddingTop: 10 }}>
                    {columnAverages.map((total, i) => (
                      <Text
                        key={i}
                        style={{
                          width: variableColumnWidthsArray[i],
                          fontSize: sumAndAvgFontSize,
                          paddingRight: 5,
                        }}
                      >
                        {total >= 0
                          ? total.toFixed(2).replace(/(\.\d*[1-9])0+$/, "$1")
                          : "--"}
                      </Text>
                    ))}
                  </View>
                </>
              )}
            </Fragment>
          )}
        </Page>,
      );
    }

    return <Document title={title}>{pages}</Document>;
  };

  const previewDoc = useMemo(() => {
    return createPDFDocument({
      title,
      headings,
      data: previewData,
      columnWidth,
      fontSize,
      startDate,
      endDate,
      isDownload: false,
      footerLabel: previewFooterLabel,
    });
  }, [
    title,
    headings,
    previewData,
    previewFooterLabel,
    columnWidth,
    fontSize,
    startDate,
    endDate,
    columnWidthTypeRadioValue,
    headingsAndDataFontSize,
  ]);

  // ✅ New async download function
  const handleDownload = async () => {
    const fullDocInstance = createPDFDocument({
      title,
      headings,
      data,
      columnWidth,
      fontSize,
      startDate,
      endDate,
      isDownload: true,
    });

    const asPdf = pdf([]);
    asPdf.updateContainer(fullDocInstance);
    const blob = await asPdf.toBlob();
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = `${title.replace(/\s+/g, "_")}_Report.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <MyView>
      <Header>
        <TitleGroup>
          <BackButton src={backIcon} onClick={onBack} />
          <ReportTitle>{t("title")}</ReportTitle>
          <AntRadioGroup
            values={allowCustomWidths ? [1, 2, 3] : [1, 2]}
            labels={
              allowCustomWidths
                ? [
                    t("variableColumnLabel"),
                    t("equalColumnLabel"),
                    t("customColumnLabel"),
                  ]
                : [t("variableColumnLabel"), t("equalColumnLabel")]
            }
            radioGroupClass="ant-radioGroup-000"
            onChange={(e) => setColumnWidthTypeRadioValue(e.target.value)}
            value={columnWidthTypeRadioValue}
          />
          <div className={styles.PDFReportViewer__fontSizeRadioGroupContainer}>
            <AntRadioGroup
              values={[1, 2]}
              labels={[t("largeFontLabel"), t("smallFontLabel")]}
              radioGroupClass="ant-radioGroup-000"
              onChange={(e) => {
                const value = e.target.value;
                setFontSizeRadioValue(value);
                setHeadingsAndDataFontSize(value === 1 ? 10 : 8);
                setSumAndAvgFontSize(value === 1 ? 12 : 10);
              }}
              value={fontSizeRadioValue}
            />
          </div>
        </TitleGroup>

        {/* ✅ Button triggers full PDF generation on demand */}
        <Button
          onClick={handleDownload}
          labelName="Download PDF"
          isPrimary={true}
        >
          Download PDF
        </Button>
      </Header>

      {/* JFCRX-7643. The plugin toolbar's page count can only describe the
          ten-row blob on screen, so it is hidden in the preview. */}
      <PDFViewer width="100%" height="86%" showToolbar={false}>
        {previewDoc}
      </PDFViewer>

      <div
        style={{
          marginTop: "20px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: "10px",
        }}
      >
        <Pagination
          defaultCurrent={1}
          total={data.length}
          pageSize={PDF_PREVIEW_ROWS}
          current={currentPage + 1}
          onChange={(page) => setCurrentPage(page - 1)}
          showSizeChanger={false}
        />
      </div>
    </MyView>
  );
};

export default PDFReportViewer;
